import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/i18n";
import { usePageMeta, shortDate } from "@/lib/format";
import { Spinner } from "@/components/Spinner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import VoiceRecorderButton from "@/components/VoiceRecorderButton";
import { toast } from "sonner";
import {
  ChevronLeft, MoreHorizontal, BarChart3, Plus, Sparkles, Leaf, Compass, Users,
  ChevronRight, Loader2, List,
} from "lucide-react";

const SCALE = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
const STATUS_STYLE = {
  active: "bg-primary/15 text-primary", completed: "bg-emerald-100 text-emerald-700",
  paused: "bg-amber-100 text-amber-700", archived: "bg-secondary text-muted-foreground",
};

function parseAnalysis(raw) {
  if (!raw) return null;
  if (typeof raw !== "string") return raw;
  try { return JSON.parse(raw); } catch { return { reflection: raw }; }
}

export default function GoalDetail() {
  const { id } = useParams();
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  usePageMeta(t("goals_section_title"));
  const [goal, setGoal] = useState(undefined);
  const [logOpen, setLogOpen] = useState(false);
  const [logScale, setLogScale] = useState(null);
  const [logNote, setLogNote] = useState("");
  const [logBusy, setLogBusy] = useState(false);
  const [wevaBusy, setWevaBusy] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/goals/${id}`); setGoal(data); }
    catch { setGoal(null); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const patch = async (body) => {
    try { const { data } = await api.patch(`/goals/${id}`, body); setGoal(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveProgress = async () => {
    if (!logScale) return;
    setLogBusy(true);
    try {
      const { data } = await api.post(`/goals/${id}/progress`, { scale: logScale, note: logNote.trim() || undefined });
      setGoal(data); setLogOpen(false); setLogScale(null); setLogNote("");
    } catch (e) { toast.error(apiError(e)); } finally { setLogBusy(false); }
  };

  const runWeva = async () => {
    if (!goal?.weva_analysis_enabled) { toast.error(t("goals_weva_enable_first_body")); return; }
    setWevaBusy(true);
    try { await api.post(`/goals/${id}/weva-analyze`); await load(); }
    catch (e) {
      if (e?.response?.status === 402 || e?.response?.status === 429) toast.error(t("weva_credits_needed"));
      else toast.error(apiError(e));
    } finally { setWevaBusy(false); }
  };

  const del = async () => {
    if (!window.confirm(t("goals_actions_body") || "Delete this goal?")) return;
    try { await api.delete(`/goals/${id}`); navigate("/app/goals", { replace: true }); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (goal === undefined) return <Spinner label={t("common.loading")} />;
  if (!goal) return <div className="py-16 text-center text-sm text-muted-foreground">{t("goals_section_title")}</div>;

  const a = goal.answers || {};
  const log = goal.progress_log || [];
  const last = log[log.length - 1];
  const analysis = parseAnalysis(goal.weva_last_analysis);
  const recap = [
    ["goals_q1_short", a.q1_introspection], ["goals_q2_short", a.q2_specificity],
    ["goals_q3_short", a.q3_measurability_baseline ? `${a.q3_measurability_baseline} / 10` : ""],
    ["goals_q4_short", a.q4_attractiveness], ["goals_q5_short", a.q5_relevance],
    ["goals_q6_short", a.q6_timeframe], ["goals_q7_short", a.q7_commitment],
  ];

  return (
    <div className="space-y-4" data-testid="goal-detail-screen">
      <div className="flex items-center gap-2">
        <button onClick={() => navigate("/app/goals")} data-testid="goal-detail-back" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 text-foreground">
          <ChevronLeft className="h-5 w-5" />
        </button>
        <h1 className="flex-1 font-display text-xl text-foreground">{goal.title}</h1>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button data-testid="goal-detail-more" className="flex h-9 w-9 items-center justify-center rounded-full bg-secondary/60 text-foreground"><MoreHorizontal className="h-5 w-5" /></button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => patch({ status: "completed" })} data-testid="goal-complete">{t("goals_action_complete")}</DropdownMenuItem>
            <DropdownMenuItem onClick={() => patch({ status: "archived" })} data-testid="goal-archive">{t("goals_action_archive")}</DropdownMenuItem>
            <DropdownMenuItem onClick={del} className="text-rose-600" data-testid="goal-delete">{t("jr.delete") || "Delete"}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="flex items-center gap-2">
        <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${STATUS_STYLE[goal.status] || STATUS_STYLE.active}`}>{t(`goals_status_${goal.status}`)}</span>
        {a.q6_timeframe ? <span className="text-xs text-muted-foreground">{a.q6_timeframe}</span> : null}
      </div>

      {/* Progress card */}
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="mb-2 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-primary" /><span className="font-display text-sm text-foreground">{t("goals_progress_card")}</span></div>
        {log.length === 0 ? (
          <p className="text-sm italic text-muted-foreground">{t("goals_no_progress_yet")}</p>
        ) : (
          <div>
            <div className="flex items-baseline gap-2">
              <span className="font-display text-3xl text-primary">{last.scale}<span className="text-sm text-muted-foreground">/10</span></span>
              <span className="text-xs text-muted-foreground">{shortDate(last.logged_at)}</span>
            </div>
            {last.note ? <p className="mt-1 text-sm italic text-muted-foreground">&ldquo;{last.note}&rdquo;</p> : null}
            <div className="mt-3 flex h-10 items-end gap-1">
              {log.slice(-14).map((e, i) => (
                <div key={i} className="flex-1 rounded-sm bg-primary" style={{ height: `${10 + (e.scale / 10) * 90}%`, opacity: 0.35 + (e.scale / 10) * 0.65 }} />
              ))}
            </div>
            <p className="mt-1 text-[10px] text-muted-foreground">{t("goals_progress_last_n", { n: Math.min(14, log.length) })}</p>
          </div>
        )}
        <Button variant="outline" onClick={() => setLogOpen(true)} data-testid="goal-log-progress" className="mt-3 gap-1.5 rounded-full">
          <Plus className="h-4 w-4" /> {t("goals_log_progress")}
        </Button>
      </div>

      {/* WEVA card */}
      <div className="rounded-2xl border border-primary/30 bg-primary/5 p-4">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="inline-flex items-center gap-1.5 font-display text-sm text-foreground"><Sparkles className="h-4 w-4 text-primary" /> {t("goals_weva_card_title")}</span>
          <Switch checked={goal.weva_analysis_enabled} onCheckedChange={(v) => patch({ weva_analysis_enabled: v })} data-testid="goal-weva-toggle-detail" />
        </div>
        <p className="text-xs text-muted-foreground">{t("goals_weva_enable_body")}</p>
        {goal.weva_analysis_enabled ? (
          <>
            {analysis?.reflection ? (
              <div className="mt-3 rounded-xl border border-border bg-card p-3">
                <p className="text-sm italic text-foreground">&ldquo;{analysis.reflection}&rdquo;</p>
                {Array.isArray(analysis.strengths) && analysis.strengths.length > 0 ? (
                  <>
                    <p className="mt-3 text-[10px] font-semibold uppercase tracking-wide text-primary">{t("goals_weva_strengths")}</p>
                    {analysis.strengths.map((sx, i) => <div key={i} className="mt-1 flex gap-2 text-sm text-foreground"><Leaf className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /><span>{sx}</span></div>)}
                  </>
                ) : null}
                {Array.isArray(analysis.gentle_challenges) && analysis.gentle_challenges.length > 0 ? (
                  <>
                    <p className="mt-3 text-[10px] font-semibold uppercase tracking-wide text-primary">{t("goals_weva_challenges")}</p>
                    {analysis.gentle_challenges.map((c, i) => <div key={i} className="mt-1 flex gap-2 text-sm text-foreground"><Compass className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" /><span>{c}</span></div>)}
                  </>
                ) : null}
                {analysis.next_step ? (
                  <>
                    <p className="mt-3 text-[10px] font-semibold uppercase tracking-wide text-primary">{t("goals_weva_next_step")}</p>
                    <p className="mt-1 rounded-lg bg-primary/10 p-2 text-sm text-foreground">{analysis.next_step}</p>
                  </>
                ) : null}
                {goal.weva_last_analysis_at ? <p className="mt-2 text-right text-[10px] text-muted-foreground">{shortDate(goal.weva_last_analysis_at)}</p> : null}
              </div>
            ) : (
              <p className="mt-2 text-sm italic text-muted-foreground">{t("goals_weva_no_analysis_yet")}</p>
            )}
            <Button onClick={runWeva} disabled={wevaBusy} data-testid="goal-weva-analyze" className="mt-3 w-full gap-2 rounded-full">
              {wevaBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {analysis?.reflection ? t("goals_weva_regenerate") : t("goals_weva_analyze")}
            </Button>
          </>
        ) : null}
      </div>

      {/* Journey with a coach */}
      <button onClick={() => navigate("/app/coach-hub")} data-testid="goal-action-coach" className="flex w-full items-center gap-3 rounded-2xl border border-primary/40 bg-card p-4 text-left transition-colors hover:bg-primary/5">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10"><Users className="h-5 w-5 text-primary" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-foreground">{t("goals_coach_cta_title")}</p>
          <p className="text-xs text-muted-foreground">{t("goals_coach_cta_sub")}</p>
        </div>
        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
      </button>

      {/* Framework recap */}
      <div className="rounded-2xl border border-border bg-card p-4">
        <div className="mb-1 flex items-center gap-2"><List className="h-4 w-4 text-foreground" /><span className="font-display text-sm text-foreground">{t("goals_framework_recap")}</span></div>
        {recap.map(([k, v]) => (
          <div key={k} className="border-t border-border py-2.5 first:border-0">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{t(k)}</p>
            <p className="mt-0.5 text-sm text-foreground">{(v || "").toString().trim() || "—"}</p>
          </div>
        ))}
      </div>

      {/* Log progress dialog */}
      <Dialog open={logOpen} onOpenChange={setLogOpen}>
        <DialogContent className="max-w-md" data-testid="goal-log-dialog">
          <DialogHeader><DialogTitle>{t("goals_log_progress")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("goals_log_prompt")}</p>
          <div className="flex flex-wrap justify-center gap-2">
            {SCALE.map((n) => (
              <button key={n} onClick={() => setLogScale(n)} data-testid={`log-scale-${n}`}
                className={`h-10 w-10 rounded-full border text-sm font-semibold transition-colors ${logScale === n ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card text-foreground"}`}>{n}</button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <VoiceRecorderButton compact onTranscribed={(txt) => setLogNote((p) => (p ? p + " " : "") + txt)} lang={lang} />
            <span className="text-xs text-muted-foreground">{t("goal_voice_hint")}</span>
          </div>
          <Textarea value={logNote} onChange={(e) => setLogNote(e.target.value)} placeholder={t("goals_log_note_ph")} className="min-h-[70px]" maxLength={1000} data-testid="log-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setLogOpen(false)} className="rounded-full">{t("cancel")}</Button>
            <Button onClick={saveProgress} disabled={!logScale || logBusy} data-testid="log-save" className="rounded-full">
              {logBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
